/**
 * Audit history: who changed what, when (MOSA-368 slice 1).
 *
 * The API is the rule. It decides who may read the Activity list (the owner)
 * and the row history (anyone who may see the row), and it writes the events.
 * What lives here is the shape on the wire, the copy, and the two headers the
 * panel sends so a bulk edit or a CSV import reads as one entry rather than as
 * a run of single-row writes.
 */

export type AuditAction =
  | "EDIT"
  | "ADD"
  | "DELETE"
  | "BULK"
  | "IMPORT"
  | "INVITE_SENT"
  | "INVITE_ACCEPTED"
  | "ROLE_CHANGE"
  | "TAB_ACCESS"
  | "ROW_FILTER"
  | "MEMBER_REMOVED"
  | "DENIED_READ";
export type AuditResult = "OK" | "DENIED";

export type AuditFieldChange = {
  field: string;
  old: string | null;
  new: string | null;
};

export type AuditRow = {
  rowKey: string | null;
  changes: AuditFieldChange[];
};

export type AuditEvent = {
  id: string;
  createdAt: string;
  tab: string;
  action: AuditAction;
  result: AuditResult;
  source: string;
  actor: { userId: string | null; email: string | null; name: string | null } | null;
  rowCount: number;
  rows: AuditRow[];
  deniedReason: string | null;
};

export type AuditPage = {
  items: AuditEvent[];
  total: number;
  page: number;
  limit: number;
};

/** Entries per page of the Activity list, and the API maximum. */
export const ACTIVITY_PAGE_SIZE = 25;

/** Where the Activity link goes. */
export const activityPath = (datasourceId: string): string =>
  `/datasources/${datasourceId}/activity`;

/* ------------------------------------------------------- write headers */

/** Groups the rows of one user action into one Activity entry. */
export const AUDIT_BATCH_HEADER = "x-onlydash-batch";

/** Labels a write the HTTP verb alone cannot name. */
export const AUDIT_ACTION_HEADER = "x-onlydash-action";

/**
 * A batch id for one user action.
 *
 * The panel's bulk edit, bulk delete and CSV import are each many single-row
 * calls, so without this the API would see a run of ordinary edits and the
 * owner would read 50 entries where one change was made.
 *
 * `crypto.randomUUID` is not in every browser the app supports, so this falls
 * back to a random token of the same shape the API accepts.
 */
export const newBatchId = (): string => {
  const uuid = globalThis.crypto?.randomUUID;
  if (typeof uuid === "function") return globalThis.crypto.randomUUID();
  return `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/** The headers one write of a labelled batch carries. */
export const auditWriteHeaders = (batch?: {
  batchId: string;
  action: "bulk" | "import";
}): Record<string, string> =>
  batch
    ? {
        [AUDIT_BATCH_HEADER]: batch.batchId,
        [AUDIT_ACTION_HEADER]: batch.action,
      }
    : {};

/* --------------------------------------------------------------- copy */

/** Until the Google Sheets slice ships, every event came from OnlyDash. */
export const AUDIT_SOURCE_NOTE = "Changes made in OnlyDash.";

export const auditActionLabel = (event: {
  action: AuditAction;
  rowCount: number;
}): string => {
  const rows = `${event.rowCount} ${event.rowCount === 1 ? "row" : "rows"}`;
  switch (event.action) {
    case "ADD":
      return "Added a row";
    case "DELETE":
      return event.rowCount === 1 ? "Deleted a row" : `Deleted ${rows}`;
    case "BULK":
      return `Edited ${rows}`;
    case "IMPORT":
      return `Imported ${rows}`;
    default:
      return "Edited a row";
  }
};

/** Why a write was refused, in words the owner can act on. */
export const auditDeniedLabel = (reason: string | null): string => {
  if (reason === "view_only") return "Refused: view-only access";
  if (reason === "datasource_read_only") return "Refused: the panel is read-only";
  return "Refused";
};

/** Who made the change. A removed member is still named by the event. */
export const auditActorLabel = (event: AuditEvent): string =>
  event.actor?.name || event.actor?.email || "Someone";

/** A stored cell value, or the word for having none. */
export const auditValueLabel = (value: string | null): string =>
  value === null || value === "" ? "empty" : value;

/**
 * When, in the reader's own time zone.
 *
 * The API stores UTC. `toLocaleString` with no locale follows the browser, so
 * a reader in Helsinki and a reader in Lisbon each see their own clock.
 */
export const auditTimeLabel = (createdAt: string): string => {
  const at = new Date(createdAt);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

/** The rows of an entry that actually say what changed. */
export const changedRows = (event: AuditEvent): AuditRow[] =>
  (event.rows ?? []).filter((row) => row.changes?.length > 0);

/** One line per changed field: `Status: Open → Closed`. */
export const fieldChangeLine = (change: AuditFieldChange): string =>
  `${change.field}: ${auditValueLabel(change.old)} → ${auditValueLabel(
    change.new
  )}`;

const ACCESS_ACTIONS = new Set<AuditAction>([
  "INVITE_SENT",
  "INVITE_ACCEPTED",
  "ROLE_CHANGE",
  "TAB_ACCESS",
  "ROW_FILTER",
  "MEMBER_REMOVED",
  "DENIED_READ",
]);

const ROLE_WORDS: Record<string, string> = {
  viewer: "Viewer",
  editor: "Editor",
  custom: "Custom",
  owner: "Owner",
};

const LEVEL_WORDS: Record<string, string> = {
  none: "No access",
  view: "View",
  edit: "Edit",
};

const eventChanges = (event: AuditEvent): AuditFieldChange[] =>
  (event.rows ?? []).flatMap((row) => row.changes ?? []);

/** The other person. Removal stores the email on `old`. */
const memberLabel = (event: AuditEvent): string => {
  const member = eventChanges(event).find((change) => change.field === "member");
  const value = event.action === "MEMBER_REMOVED" ? member?.old : member?.new;
  const email = typeof value === "string" ? value.trim() : "";
  return email || "someone";
};

const roleWord = (value: string | null | undefined): string => {
  if (!value) return "";
  return ROLE_WORDS[value] ?? value;
};

const levelWord = (value: string | null | undefined): string => {
  if (!value) return "No access";
  return LEVEL_WORDS[value] ?? value;
};

const fieldName = (prefix: string, field: string): string =>
  field.startsWith(prefix) ? field.slice(prefix.length) : "";

/**
 * The access-change or denied-read sentence.
 *
 * Null for a row write, so the Activity list keeps the slice 1 headline.
 * The app does not drop events. A row write stays on the old path.
 */
export const auditAccessSentence = (event: AuditEvent): string | null => {
  if (!ACCESS_ACTIONS.has(event.action)) return null;
  const actor = auditActorLabel(event);
  const member = memberLabel(event);
  const changes = eventChanges(event);

  switch (event.action) {
    case "INVITE_SENT":
      return `${actor} invited ${member}`;
    case "INVITE_ACCEPTED":
      return `${actor} accepted the invite`;
    case "MEMBER_REMOVED":
      return `${actor} removed ${member}`;
    case "ROLE_CHANGE": {
      const role = changes.find((change) => change.field === "role");
      const from = roleWord(role?.old);
      const to = roleWord(role?.new);
      if (from && to) return `${actor} changed ${member} from ${from} to ${to}`;
      if (to) return `${actor} changed ${member} to ${to}`;
      if (from) return `${actor} changed ${member} from ${from}`;
      return `${actor} changed the role of ${member}`;
    }
    case "TAB_ACCESS": {
      const tabs = changes.filter((change) => change.field.startsWith("tab:"));
      if (tabs.length === 0) {
        return `${actor} changed ${member}'s access to ${event.tab || "a table"}`;
      }
      return tabs
        .map((change) => {
          const tab = fieldName("tab:", change.field) || event.tab || "a table";
          return `${actor} changed ${member}'s access to ${tab}: ${levelWord(
            change.old
          )} → ${levelWord(change.new)}`;
        })
        .join(". ");
    }
    case "ROW_FILTER": {
      const filters = changes.filter((change) =>
        change.field.startsWith("filter:")
      );
      const tabs = filters.length
        ? filters.map(
            (change) =>
              fieldName("filter:", change.field) || event.tab || "a table"
          )
        : [event.tab || "a table"];
      return tabs
        .map((tab) => `${actor} changed ${member}'s row filter on ${tab}`)
        .join(". ");
    }
    case "DENIED_READ": {
      const tab = event.tab || "a table";
      const hiddenRow = (event.rows ?? []).some((row) => Boolean(row.rowKey));
      return hiddenRow
        ? `${actor} was refused a hidden row in ${tab}`
        : `${actor} was refused the hidden table ${tab}`;
    }
    default:
      return null;
  }
};

/**
 * The filter text under a row-filter event.
 *
 * Null on the wire means every row of that table. A denied read stores no
 * cell values, so this stays empty for every other action.
 */
export const auditFilterLines = (event: AuditEvent): string[] => {
  if (event.action !== "ROW_FILTER") return [];
  return eventChanges(event)
    .filter((change) => change.field.startsWith("filter:"))
    .map((change) => {
      const tab = fieldName("filter:", change.field) || event.tab || "a table";
      const before = change.old && change.old.trim() ? change.old : "every row";
      const after = change.new && change.new.trim() ? change.new : "every row";
      return `${tab}: ${before} → ${after}`;
    });
};

/**
 * Read a page of events off the wire.
 *
 * Tolerant on purpose: the Activity list is a read-only report, so a field the
 * API renames should leave the page empty rather than throw inside a render.
 */
export const asAuditPage = (body: unknown): AuditPage => {
  const page = (body ?? {}) as Partial<AuditPage>;
  return {
    items: Array.isArray(page.items) ? page.items : [],
    total: Number(page.total ?? 0),
    page: Number(page.page ?? 1),
    limit: Number(page.limit ?? ACTIVITY_PAGE_SIZE),
  };
};

/** Pages of a total, for the "Page 2 of 4" line and the Next button. */
export const pageCount = (total: number, limit: number): number =>
  limit > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;
